import { useMemo, useCallback } from 'react';
import { Button, Modal } from 'antd';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { Form, confirmation } from '@/components';
import { ImportTasksModalInitialValue } from '@/views';
import {
  TaskTemplateMatrixState,
  useGetTaskTemplateToImportQuery,
} from '@/redux';
import { ImportPathTemplateTasksTable } from './ImportPathTemplateTasksTable';
import { PathTemplateTaskDto, PathTemplateTaskGroupDto } from '@/core';

export type TemplateTaskToImportDto = Omit<PathTemplateTaskDto, 'groupId'> & {
  checked: boolean;
  groupId: string;
  groupName: string;
};

export interface ImportPathTemplateTaskFormValue {
  rows: TemplateTaskToImportDto[];
}

interface ImportTasksModalProps {
  title: string;
  value?: ImportTasksModalInitialValue;
  onSubmit: (props: {
    groups: PathTemplateTaskGroupDto[];
    tasks: PathTemplateTaskDto[];
  }) => void;
  onClose: () => void;
  initialValue?: TaskTemplateMatrixState;
  communityId: string | undefined;
}

const MAX_VALUE = 100_000_000;

const validationSchema = Yup.object({
  rows: Yup.array()
    .of(
      Yup.object().shape({
        checked: Yup.boolean(),
        name: Yup.string().required(),
        costEstimate: Yup.number().min(0).max(MAX_VALUE).nullable(),
        groupId: Yup.string().required(),
        groupName: Yup.string().required(),
      }),
    )
    .required(),
});

export function ImportPathTemplateTasksModal(props: ImportTasksModalProps) {
  const { title, value, onSubmit, onClose, communityId } = props;
  const { t } = useTranslation();

  const templateIds = value?.templateIds ?? [];

  const { data, isFetching } = useGetTaskTemplateToImportQuery({ templateIds });

  const initialValues = useMemo<ImportPathTemplateTaskFormValue | null>(() => {
    if (!data) return null;

    return {
      rows: data.taskTemplates.flatMap((template) =>
        template.rows.map((row) => ({
          checked: false,
          name: row.name,
          costEstimate: row.costEstimate,
          groupId: template.id,
          groupName: template.name,
          responsiblePerson: row.responsiblePerson,
        })),
      ),
    };
  }, [data]);

  const handleSubmit = useCallback(
    (form: ImportPathTemplateTaskFormValue) => {
      const checkedRows = form.rows.filter((x) => x.checked);

      if (checkedRows.length === 0) {
        confirmation.show({
          title: t('tasks.import.submitTitle'),
          body: t('tasks.import.submitMessage'),
          onConfirm: () => onClose(),
        });
        return;
      }

      const groupsMap = new Map<string, { id: string; name: string }>();
      const tasks: PathTemplateTaskDto[] = [];

      for (const row of checkedRows) {
        if (!groupsMap.has(row.groupId)) {
          groupsMap.set(row.groupId, {
            id: row.groupId,
            name: row.groupName,
          });
        }

        tasks.push({
          name: row.name,
          groupId: row.groupId,
          costEstimate: row.costEstimate,
          // responsiblePerson: parseResponsiblePerson(row),
        });
      }

      onSubmit({
        groups: Array.from(groupsMap.values()),
        tasks,
      });

      onClose();
    },
    [onSubmit, onClose, t],
  );

  const handleClose = useCallback(() => {
    confirmation.show({
      title: t('tasks.import.closeTitle'),
      body: t('tasks.import.closeMessage'),
      onConfirm: () => onClose(),
    });
  }, [onClose, t]);

  return (
    <Modal
      title={title}
      visible
      onCancel={handleClose}
      width={1500}
      footer={[
        <Button key="back" onClick={handleClose}>
          {t('tasks.details.cancel')}
        </Button>,
        <Form.Submit key="submit" uid="import-tasks" type="primary">
          {t('tasks.details.save')}
        </Form.Submit>,
      ]}
    >
      {initialValues && (
        <Form.Formik<ImportPathTemplateTaskFormValue>
          uid="import-tasks"
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <ImportPathTemplateTasksTable
            name="rows"
            data={data!.taskTemplates}
            communityId={communityId}
            isFetching={isFetching}
          />
        </Form.Formik>
      )}
    </Modal>
  );
}
